import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import './RolesScreen.css';

interface Permission {
    id: number;
    key: string;
    description?: string;
}

interface Role {
    id: number;
    name: string;
    description?: string;
    permissions?: Permission[];
}

const RolesScreen: React.FC = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRoleId, setExpandedRoleId] = useState<number | null>(null);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/v1/admin/roles`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setRoles(data);
            } else if (response.status === 401) {
                setError('Session expirée. Veuillez vous reconnecter.');
            } else if (response.status === 403) {
                setError('Accès interdit. Vous devez être administrateur.');
            } else {
                setError('Erreur lors du chargement des rôles.');
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchRoles();
        }
    }, [token, fetchRoles]);

    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const toggleExpand = (roleId: number) => {
        setExpandedRoleId(expandedRoleId === roleId ? null : roleId);
    };

    const getRoleIcon = (name: string) => {
        if (name.includes('admin') || name.includes('global')) return 'shield';
        if (name.includes('support')) return 'support_agent';
        if (name.includes('pro') || name.includes('premium')) return 'star';
        return 'person';
    };

    const getRoleColor = (name: string) => {
        if (name.includes('global_admin')) return '#ef4444';
        if (name.includes('admin')) return '#8b5cf6';
        if (name.includes('pro') || name.includes('premium')) return '#f59e0b';
        return '#3b82f6';
    };

    return (
        <SiteLayout>
            <Header icon='admin_panel_settings' title='Gestion des Rôles' />
            <div className="roles-page">
                {/* Header */}
                <div className="roles-header">
                    <div className="header-info">
                        <h1>Gestion des Rôles</h1>
                        <p>Définir les rôles et assigner les permissions aux utilisateurs.</p>
                    </div>
                    <div className="header-actions">
                        <div className="search-box">
                            <i className="material-icons">search</i>
                            <input
                                type="text"
                                placeholder="Rechercher un rôle..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="btn-add" onClick={() => navigate('/admin/roles/new')}>
                            <i className="material-icons">add</i>
                            Nouveau Rôle
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="roles-content">
                    {loading ? (
                        <div className="state-container">
                            <div className="loading-spinner"></div>
                            <p>Chargement des rôles...</p>
                        </div>
                    ) : error ? (
                        <div className="state-container error">
                            <i className="material-icons">error_outline</i>
                            <h3>Erreur</h3>
                            <p>{error}</p>
                            <button className="btn-retry" onClick={fetchRoles}>
                                <i className="material-icons">refresh</i>
                                Réessayer
                            </button>
                        </div>
                    ) : filteredRoles.length === 0 ? (
                        <div className="state-container empty">
                            <i className="material-icons">group_off</i>
                            <h3>Aucun rôle trouvé</h3>
                            <p>Créez un nouveau rôle pour commencer.</p>
                        </div>
                    ) : (
                        <div className="roles-list">
                            {filteredRoles.map(role => (
                                <div key={role.id} className={`role-card ${expandedRoleId === role.id ? 'expanded' : ''}`}>
                                    <div className="role-card-header" onClick={() => toggleExpand(role.id)}>
                                        <div className="role-icon" style={{ background: getRoleColor(role.name) }}>
                                            <i className="material-icons">{getRoleIcon(role.name)}</i>
                                        </div>
                                        <div className="role-info">
                                            <h3>{role.name}</h3>
                                            <p>{role.description || 'Aucune description'}</p>
                                        </div>
                                        <div className="role-meta">
                                            <span className="permissions-count">
                                                <i className="material-icons">key</i>
                                                {role.permissions?.length || 0} permissions
                                            </span>
                                            <i className="material-icons expand-icon">
                                                {expandedRoleId === role.id ? 'expand_less' : 'expand_more'}
                                            </i>
                                        </div>
                                    </div>

                                    {expandedRoleId === role.id && (
                                        <div className="role-card-body">
                                            <div className="permissions-section">
                                                <h4>
                                                    <i className="material-icons">vpn_key</i>
                                                    Permissions attribuées
                                                </h4>
                                                {role.permissions && role.permissions.length > 0 ? (
                                                    <div className="permissions-grid">
                                                        {role.permissions.map(perm => (
                                                            <div key={perm.id} className="permission-chip">
                                                                <i className="material-icons">check_circle</i>
                                                                <span>{perm.key}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="no-permissions">Aucune permission attribuée à ce rôle.</p>
                                                )}
                                            </div>
                                            <div className="role-actions">
                                                <button
                                                    className="btn-edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/admin/roles/${role.id}`);
                                                    }}
                                                >
                                                    <i className="material-icons">edit</i>
                                                    Modifier le rôle
                                                </button>
                                                <button
                                                    className="btn-permissions"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/admin/roles/${role.id}`);
                                                    }}
                                                >
                                                    <i className="material-icons">settings</i>
                                                    Gérer les permissions
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Stats Footer */}
                {!loading && !error && roles.length > 0 && (
                    <div className="roles-footer">
                        <div className="stat">
                            <span className="stat-number">{roles.length}</span>
                            <span className="stat-label">Rôles</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">
                                {roles.reduce((acc, role) => acc + (role.permissions?.length || 0), 0)}
                            </span>
                            <span className="stat-label">Permissions assignées</span>
                        </div>
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default RolesScreen;
